import React, { useState, useEffect, useCallback, useRef } from "react";
import { connect } from "react-redux";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { Button, Form, Tooltip, Input, Space, Radio, Typography } from "antd";
import {
  InfoCircleOutlined,
  PlusOutlined,
  CloseOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { userHasRole } from "../../selectors";
import RegistrationConfirmationModal from "../manage-application/RegistrationConfirmationModal";
import style from "./OIDCRegForm.module.css";
import PropTypes from "prop-types";

const { Title, Text, Paragraph } = Typography;

const OIDCRegForm = ({
  submitFunction,
  initialValues,
  homePage,
  formValidation = null,
  handleFormSubmitClick = null,
  isNewApplicationForm = false,
  onValidationChange = null,
}) => {
  const [newUri, setNewUri] = useState("");
  const [newUriValid, setNewUriValid] = useState(false);
  const [values, setValues] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  const inputRef = useRef(null);

  const isValidHttpUrl = useCallback((value) => {
    try {
      const url = new URL(value);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  }, []);

  const uriValidator = useCallback(
    (value) => {
      if (value === "urn:ietf:wg:oauth:2.0:oob") return true;
      if (isValidHttpUrl(value)) return true;

      const isCernUri =
        /^ch\.cern(?:\.[\w.-]+)*:\/[\w\-._~:/?#[\]@!$&'()*+,;=]*$/.test(value);
      return isCernUri;
    },
    [isValidHttpUrl],
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isValid }, // <-- Add isValid here
    reset,
  } = useForm({
    mode: "all",
    defaultValues: {
      clientType: "confidential",
      serviceAccountsEnabled: false,
      baseUrl: homePage || "",
      redirectUris: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "redirectUris",
  });

  useEffect(() => {
    if (initialValues) {
      reset({
        clientType: initialValues.publicClient
          ? "public"
          : initialValues.serviceAccountsEnabled
            ? "creds"
            : "confidential",
        baseUrl: initialValues.baseUrl ?? "",
        redirectUris: (initialValues.redirectUris || []).map((uri) =>
          typeof uri === "string" ? { uri } : uri,
        ),
      });
    }
  }, [initialValues, reset]);

  useEffect(() => {
    const isFormValid = isValid && fields.length > 0;
    if (formValidation) {
      formValidation(() => isFormValid);
    }
    if (onValidationChange) {
      onValidationChange(isFormValid);
    }
  }, [isValid, fields.length, formValidation, onValidationChange]);

  const onSubmitRequests = (submitValues) => {
    // publicClient and implicitFlow are conceptually identical for us
    console.log("submitValues.clientType", submitValues.clientType);
    let data = {
      publicClient: submitValues.clientType === "public",
      implicitFlowEnabled: submitValues.clientType === "public",
      serviceAccountsEnabled: submitValues.clientType === "creds",
      baseUrl: submitValues.baseUrl,
      redirectUris: submitValues.redirectUris.map((r) => r.uri),
    };
    console.log("data", data);
    if (initialValues) {
      submitFunction({ ...initialValues, ...data });
    } else {
      submitFunction({ ...data });
    }
  };

  const onSubmit = (data) => {
    setValues(data);
    if (initialValues) {
      onSubmitRequests(data);
    } else {
      setConfirmationOpen(true);
    }
    return;
  };

  const onSubmitConfirmation = () => {
    setConfirmationOpen(false);
    if (values) {
      onSubmitRequests(values);
    } else {
      console.error("No stored form data available for submission.");
    }
  };

  const disableEnter = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  useEffect(() => {
    // Push the focus into the input box
    if (!newUri) {
      inputRef.current.focus();
    }
    setNewUriValid(uriValidator(newUri));
  }, [newUri, uriValidator]);

  useEffect(
    () => {
      if (handleSubmit && handleFormSubmitClick) {
        handleFormSubmitClick(() => handleSubmit(onSubmit));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleSubmit, handleFormSubmitClick],
  );

  const addNewUri = () => {
    if (!newUri || newUri === "" || !uriValidator(newUri)) {
      return;
    } else {
      append({
        uri: newUri,
      });
      setNewUri("");
    }
  };

  return (
    <>
      <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
        <Title level={5} className={style["section-header"]}>
          Redirect URI(s) <Text type="danger">*</Text>
        </Title>

        <Paragraph className={style["helper-text"]}>
          Specify the URI(s) where users will be redirected after
          authenticating, e.g.<Text code>https://test.cern.ch/*</Text>. Native
          apps should start with<Text code>ch.cern</Text>, e.g.
          <Text code>ch.cern.myapp:/oauth2redirect</Text>.
        </Paragraph>
        {fields.map((fieldItem, index) => (
          <Form.Item key={fieldItem.id} style={{ marginBottom: "8px" }}>
            <Controller
              name={`redirectUris[${index}].uri`}
              control={control}
              rules={{ validate: uriValidator || "Invalid URI format" }}
              render={({ field }) => (
                <Space.Compact style={{ width: "100%" }}>
                  <Input
                    {...field}
                    id="redirectURIRemoveField"
                    onKeyDown={disableEnter}
                  />
                  <Button
                    variant="outlined"
                    icon={<CloseOutlined />}
                    danger
                    onClick={() => remove(index)}
                  />
                </Space.Compact>
              )}
            />

            {errors.redirectUris?.[index]?.uri && (
              <Paragraph style={{ color: "red", marginTop: 4 }}>
                {errors.redirectUris[index].uri.message}
              </Paragraph>
            )}
          </Form.Item>
        ))}

        <Form.Item
          help={
            fields.length === 0 ? "At least one redirect URI is required" : ""
          }
          validateStatus={fields.length === 0 ? "warning" : ""}
        >
          <Space.Compact style={{ width: "100%" }}>
            <Input
              ref={inputRef}
              id="redirectURIAddField"
              placeholder="https://mysite.cern.ch/*"
              value={newUri}
              onChange={(e) => setNewUri(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addNewUri();
                }
              }}
            />
            <Button
              variant="outlined"
              color="green"
              icon={<PlusOutlined />}
              disabled={!newUriValid}
              onClick={addNewUri}
            />
          </Space.Compact>

          {newUri && !newUriValid && (
            <Paragraph style={{ color: "red", marginTop: 4, marginBottom: 0 }}>
              Invalid URL format
            </Paragraph>
          )}
        </Form.Item>

        <Title level={5} className={style["section-header"]}>
          Base URL <Text type="danger">*</Text>
          {homePage && (
            <Tooltip title="The default value is the Home Page of your application.">
              <InfoCircleOutlined
                style={{ color: "#1890ff", fontSize: "14px" }}
              />
            </Tooltip>
          )}
        </Title>

        <Form.Item
          help={errors.baseUrl?.message}
          validateStatus={errors.baseUrl ? "error" : ""}
        >
          <Paragraph className={style["helper-text"]}>
            Specify the URL that the SSO will use to redirect or link back to
            your application.
          </Paragraph>
          <Controller
            name={`baseUrl`}
            control={control}
            rules={{
              validate: {
                required: (value) => {
                  if (!value || value.trim() === "") {
                    return "Base URL is required";
                  }
                  return true;
                },
                validUrl: (value) => {
                  if (!value || value.trim() === "") {
                    return true; // Skip URL validation if empty (required handles this)
                  }
                  return isValidHttpUrl(value) || "Invalid URL format";
                },
              },
            }}
            render={({ field }) => (
              <Input
                {...field}
                ref={inputRef}
                placeholder="https://mysite.cern.ch/"
                id="baseUrlField"
                onKeyDown={disableEnter}
              />
            )}
          />
        </Form.Item>

        <Title level={5} className={style["section-header"]}>
          Client Secret Configuration{" "}
        </Title>
        <Form.Item>
          <Controller
            name="clientType"
            control={control}
            render={({ field }) => (
              <Radio.Group
                {...field}
                onChange={(e) => {
                  field.onChange(e.target.value);
                }}
                value={field.value}
              >
                <Space direction="vertical" style={{ width: "100%" }}>
                  <div>
                    <Radio value="public">Public Client</Radio>
                    <Paragraph className={style["helper-text"]}>
                      If your application cannot store a client secret safely it
                      must be configured as a Public Client. For example, it
                      could be a single page application (or similar client side
                      code). By selecting this option you will not be given a
                      client secret.
                    </Paragraph>
                  </div>

                  <div>
                    <Radio value="confidential">Confidential Client</Radio>
                    <Paragraph className={style["helper-text"]}>
                      Select this option if you can safely store a client
                      secret.
                    </Paragraph>
                  </div>

                  <div>
                    <Radio value="creds">
                      Confidential Client with Client Credential Flow
                    </Radio>
                    <Paragraph className={style["helper-text"]}>
                      Select this option if your client will acquire a token for
                      itself (rather than for users) by logging in with its own
                      client ID and secret using the Client Credentials Flow.
                      This is typically done if a client needs to access a
                      protected API.
                    </Paragraph>
                  </div>
                </Space>
              </Radio.Group>
            )}
          />
        </Form.Item>

        {!isNewApplicationForm && (
          <Form.Item className={style["submit-button"]}>
            <Button
              type="primary"
              id="oidcSubmitButton"
              htmlType="submit"
              disabled={!isValid || fields.length === 0}
              icon={<CheckOutlined />}
            >
              Submit
            </Button>
          </Form.Item>
        )}
      </Form>
      <RegistrationConfirmationModal
        visible={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onSubmit={() => onSubmitConfirmation()}
      />
    </>
  );
};

OIDCRegForm.propTypes = {
  submitFunction: PropTypes.func,
  initialValues: PropTypes.object,
  homePage: PropTypes.string,
  formValidation: PropTypes.func,
  handleFormSubmitClick: PropTypes.func,
  isNewApplicationForm: PropTypes.bool,
  onValidationChange: PropTypes.func,
};

export default connect((state) => ({
  initialValues: state.selectedRegistration.registration,
  admin: userHasRole(state, "admin"),
  homePage: state.active_application.application.homePage,
}))(OIDCRegForm);
